import { panelsOf, fromPanels, scaleParts } from "@/lib/geometry";

const uniq = (a) => [...new Set(a)].sort((p, q) => p - q);

const toC = (side, W) => (p) => (side === "top" ? { ...p, u0: p.x0, u1: p.x1, v0: p.y0, v1: p.y1 }
  : side === "left" ? { ...p, u0: p.y0, u1: p.y1, v0: p.x0, v1: p.x1 }
  : { ...p, u0: p.y0, u1: p.y1, v0: W - p.x1, v1: W - p.x0 });
const fromC = (side, W) => ({ u0, u1, v0, v1, op, glass }) => (side === "top" ? { x0: u0, x1: u1, y0: v0, y1: v1, op, glass }
  : side === "left" ? { x0: v0, x1: v1, y0: u0, y1: u1, op, glass }
  : { x0: W - v1, x1: W - v0, y0: u0, y1: u1, op, glass });

function topBand(cp, H) {
  const vs = uniq(cp.map((p) => p.v0)).filter((v) => v > 0 && v < H);
  return vs.find((v) => !cp.some((p) => p.v0 < v && p.v1 > v));
}

function context(item, side) {
  const W = item.width, H = item.height;
  const all = panelsOf(item);
  let fixed = [], region = all;
  if (side !== "top") {
    const tc = all.map(toC("top", W)), oh = topBand(tc, H);
    if (oh) { fixed = all.filter((p) => p.y1 <= oh); region = all.filter((p) => p.y0 >= oh); }
  }
  const cp = region.map(toC(side, W));
  const Lv = side === "top" ? H : W;
  const uA = Math.min(...cp.map((p) => p.u0)), uB = Math.max(...cp.map((p) => p.u1));
  return { W, fixed, cp, Lv, uA, uB };
}

const isDoor = (op) => op?.startsWith("door");

export function bandInfo(item, side) {
  const { cp, Lv, uA, uB } = context(item, side);
  const b = topBand(cp, Lv);
  if (!b) return null;
  const band = cp.filter((p) => p.v1 <= b);
  if (side !== "top" && !item.sidelights?.[side]) {
    const rest = cp.filter((p) => p.v0 >= b);
    if (band.some((p) => isDoor(p.op)) || !rest.some((p) => isDoor(p.op))) return null;
  }
  const ub = uniq(band.flatMap((p) => [p.u0, p.u1])), vb = uniq(band.flatMap((p) => [p.v0, p.v1]));
  return {
    size: b, uA, uB, band, rest: cp.filter((p) => p.v0 >= b),
    lengthParts: ub.slice(1).map((v, i) => v - ub[i]), depthParts: vb.slice(1).map((v, i) => v - vb[i]),
  };
}

const grid = (uA, lp, dp, ops) => {
  const out = [];
  let v = 0;
  dp.forEach((d, j) => {
    let u = uA;
    lp.forEach((l, i) => { out.push({ u0: u, u1: u + l, v0: v, v1: v + d, op: ops?.[j * lp.length + i] || "fixed" }); u += l; });
    v += d;
  });
  return out;
};

function rebuild(item, side, ctx, bandPanels, rest) {
  return fromPanels(item, [...ctx.fixed, ...[...bandPanels, ...rest].map(fromC(side, ctx.W))]);
}

export function setBandGrid(item, side, lp, dp) {
  const ctx = context(item, side), info = bandInfo(item, side);
  if (!info) return item;
  const ops = info.band.length === lp.length * dp.length ? [...info.band].sort((a, b) => a.v0 - b.v0 || a.u0 - b.u0).map((p) => p.op) : null;
  return rebuild(item, side, ctx, grid(info.uA, lp, dp, ops), info.rest);
}

export function setBandCount(item, side, axis, n) {
  const info = bandInfo(item, side);
  if (!info) return item;
  const k = Math.max(1, Math.min(8, n));
  const lp = axis === "length" ? scaleParts(Array(k).fill(1), info.uB - info.uA) : info.lengthParts;
  const dp = axis === "depth" ? scaleParts(Array(k).fill(1), info.size) : info.depthParts;
  return setBandGrid(item, side, lp, dp);
}

export function setBandSize(item, side, s) {
  const ctx = context(item, side), info = bandInfo(item, side);
  if (!info) return item;
  const touching = info.rest.filter((p) => p.v0 === info.size);
  const limit = Math.min(...touching.map((p) => p.v1));
  if (!(s >= 100) || s >= limit - 100) return item;
  const dp = scaleParts(info.depthParts, s);
  const ops = info.band.length === info.lengthParts.length * info.depthParts.length ? [...info.band].sort((a, b) => a.v0 - b.v0 || a.u0 - b.u0).map((p) => p.op) : null;
  return rebuild(item, side, ctx, grid(info.uA, info.lengthParts, dp, ops), info.rest.map((p) => (p.v0 === info.size ? { ...p, v0: s } : p)));
}

export function addBand(item, side, size) {
  const ctx = context(item, side);
  const touching = ctx.cp.filter((p) => p.v0 === 0);
  const s = Math.min(size, Math.round(Math.min(...touching.map((p) => p.v1)) / 2));
  if (!(s >= 100)) return item;
  const moved = ctx.cp.map((p) => (p.v0 === 0 ? { ...p, v0: s } : p));
  const next = rebuild(item, side, ctx, [{ u0: ctx.uA, u1: ctx.uB, v0: 0, v1: s, op: "fixed" }], moved);
  return side === "top" ? next : { ...next, sidelights: { ...(item.sidelights || {}), [side]: true } };
}

export function removeBand(item, side) {
  const ctx = context(item, side), info = bandInfo(item, side);
  if (!info) return item;
  const next = rebuild(item, side, ctx, [], info.rest.map((p) => (p.v0 === info.size ? { ...p, v0: 0 } : p)));
  return side === "top" ? next : { ...next, sidelights: { ...(item.sidelights || {}), [side]: false } };
}

export function bandPanelsXY(item, side) {
  const info = bandInfo(item, side);
  return info ? info.band.map(fromC(side, item.width)) : [];
}
